import { queryOptions, useQuery } from "@tanstack/react-query";

import { authClient } from "@/lib/auth-client";
import { getSsoStatus } from "@/lib/api/sso";
import { passwordChangedOnH2j } from "@/lib/sso-accounts";

export const ssoKeys = {
  all: ["sso"] as const,
  status: () => [...ssoKeys.all, "status"] as const,
  accounts: () => [...ssoKeys.all, "accounts"] as const,
};

// `endSessionUrl` in this payload belongs to whoever loaded it: the sign-out
// reads a fresh one (`fetchEndSessionUrl`), never this cached copy.
export const ssoStatusQueryOptions = queryOptions({
  queryKey: ssoKeys.status(),
  queryFn: getSsoStatus,
  staleTime: Infinity,
});

/**
 * `enabled`: the "Sign in with H2J" button shows. `ssoOnly`: the switch happened
 * and the password form is left to emergency accounts. `passwordUrl`: where the
 * Login H2J password is changed. A failed load counts as "off", so the form still shows.
 */
export function useSsoStatus() {
  const { data, isPending } = useQuery(ssoStatusQueryOptions);
  return {
    enabled: data?.enabled ?? false,
    ssoOnly: data?.ssoOnly ?? false,
    passwordUrl: data?.passwordUrl ?? null,
    isLoading: isPending,
  };
}

/**
 * Whether the signed-in account changes its password on Login H2J instead of the
 * profile (`passwordChangedOnH2j`). Accounts are only fetched when `enabled`.
 */
export function usePasswordChangedOnH2j(enabled: boolean, ssoOnly: boolean) {
  const { data } = useQuery({
    queryKey: ssoKeys.accounts(),
    // A failed load must stay "not loaded" (the form shows), never an empty list.
    queryFn: async () => {
      const res = await authClient.listAccounts();
      if (res.error) throw new Error(res.error.message ?? "Failed to load the linked accounts");
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return passwordChangedOnH2j(data, ssoOnly);
}
